import React from "react";
import { DynamicField } from "../types/DynamicField";

interface DynamicFormProps {
  fields: DynamicField[];
  visibleFields?: Record<string, boolean>;
  formData?: Record<string, any>;
  onFieldChange?: (fieldId: string, value: any) => void;
}

const DynamicForm: React.FC<DynamicFormProps> = ({
  fields,
  visibleFields = {},
  formData = {},
  onFieldChange = () => {},
}) => {
  const sections = new Map<string, DynamicField[]>();
  const sectionNames = new Map<string, string>();
  const fieldsWithoutSection: DynamicField[] = [];

  fields.forEach((field) => {
    if (field.section) {
      const id = field.section.id;
      if (!sections.has(id)) {
        sections.set(id, []);
        sectionNames.set(id, field.section.name);
      }
      sections.get(id)?.push(field);
    } else {
      fieldsWithoutSection.push(field);
    }
  });

  const isVisible = (field: DynamicField) => {
    return visibleFields[field.id] ?? true; 
  };

  const renderField = (field: DynamicField) => {
    if (!isVisible(field)) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value =
        e.target.type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : e.target.value;

      onFieldChange(field.id, value);
    };

    return (
      <div key={field.id} className="mb-3">
        <label className="form-label">{field.label}</label>

        {(field.type.value === "text" ||
          field.type.value === "number" ||
          field.type.value === "date" ||
          field.type.value === "datetime" ||
          field.type.value === "decimal") && (
          <input
            type={
              field.type.value === "decimal"
                ? "number"
                : field.type.value === "datetime"
                ? "datetime-local"
                : field.type.value
            }
            step={field.type.value === "decimal" ? "0.01" : undefined}
            className="form-control"
            name={field.label}
            placeholder={field.placeholder || ""}
            value={formData[field.id] || ""}
            onChange={handleChange}
          />
        )}

        {field.type.value === "select" && (
          <select
            className="form-select"
            name={field.label}
            value={formData[field.id] || ""}
            onChange={handleChange}
          >
            <option value="">-- Sélectionner --</option>
            {field.options?.map((opt, idx) => (
              <option key={opt} value={field.optionsValues?.[idx] || opt}>
                {opt}
              </option>
            ))}
          </select>
        )}

        {field.type.value === "radio" && (
          <div>
            {field.options?.map((opt, idx) => (
              <div className="form-check" key={opt}>
                <input
                  className="form-check-input"
                  type="radio"
                  name={field.label}
                  id={`${field.id}-${opt}`}
                  value={field.optionsValues?.[idx] || opt}
                  checked={formData[field.id] === (field.optionsValues?.[idx] || opt)}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor={`${field.id}-${opt}`}>
                  {opt}
                </label>
              </div>
            ))}
          </div>
        )}

        {field.type.value === "checkbox" && (
          <div className="d-flex flex-column">
            {field.options?.map((opt, idx) => (
              <div className="form-check" key={opt}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  name={`${field.label}[]`}
                  id={`${field.id}-${opt}`}
                  value={field.optionsValues?.[idx] || opt}
                  checked={formData[field.id]?.includes(field.optionsValues?.[idx] || opt) || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    let newValue = formData[field.id] || [];
                    if (checked) newValue = [...newValue, field.optionsValues?.[idx] || opt];
                    else newValue = newValue.filter((v: any) => v !== (field.optionsValues?.[idx] || opt));
                    onFieldChange(field.id, newValue);
                  }}
                />
                <label className="form-check-label ms-2" htmlFor={`${field.id}-${opt}`}>
                  {opt}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dynamic-form">
      {fieldsWithoutSection.length > 0 && (
        <div className="mb-3">
          <div className="row">
            {fieldsWithoutSection.map((field) => (
              <div key={field.id} className="col-12 col-md-6">
                {renderField(field)}
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.from(sections.entries()).map(([sectionId, sectionFields]) => (
        <div key={sectionId} className="mt-4 pt-3 border-top">
          <h5 className="mb-3">{sectionNames.get(sectionId)}</h5>
          <div className="ps-3">
            <div className="row">
              {sectionFields.map((field) => (
                <div key={field.id} className="col-12 col-md-6">
                  {renderField(field)}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DynamicForm;